import { Plus, Trash2, Upload, RefreshCw, Lock } from "lucide-react";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../ui/tabs";
import { Switch } from "../../ui/switch";
import { TableConfigProps } from "./types";

function generateField(label: string, index: number): string {
  const result = label
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '');
  return result || `campo_${index + 1}`;
}

export function TableConfig({
  editedComponent,
  setEditedComponent,
  updateConfig,
  availableComponents,
  sheetsIntegrations,
  restApiIntegrations,
  isSuperAdmin,
  tableDataSource,
  setTableDataSource,
  availableSheets,
  loadingSheets,
  sheetFetchError,
  handleFetchSheets,
  handleExcelUpload,
  uploadMode,
  setUploadMode,
}: TableConfigProps) {
  const columns = editedComponent.config.columns || [];
  const isGoogleSheetsActive =
    tableDataSource === "googleSheets" || tableDataSource === "restApi";

  return (
    <Tabs defaultValue="geral" className="w-full">
      <TabsList className="grid w-full grid-cols-3">
        <TabsTrigger value="geral">Geral</TabsTrigger>
        <TabsTrigger value="colunas" disabled={isGoogleSheetsActive}>
          Colunas {columns.length > 0 && `(${columns.length})`}
        </TabsTrigger>
        <TabsTrigger value="exibicao">Exibição</TabsTrigger>
      </TabsList>

      <TabsContent value="geral" className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="tableTitle">Título da Tabela</Label>
          <Input
            id="tableTitle"
            type="text"
            value={editedComponent.config.title || ""}
            onChange={(e) => updateConfig("title", e.target.value)}
            placeholder="Digite o título..."
          />
        </div>

        {/* Seletor de Fonte de Dados */}
        <div className="space-y-3">
          <Label className="text-sm font-medium">Fonte de Dados</Label>
          <div className={`grid gap-3 ${isSuperAdmin ? "grid-cols-4" : "grid-cols-3"}`}>
            <button
              type="button"
              onClick={() => {
                setTableDataSource("manual");
                updateConfig("googleSheets", {
                  ...editedComponent.config.googleSheets,
                  enabled: false,
                });
                updateConfig("restApi", {
                  ...editedComponent.config.restApi,
                  enabled: false,
                });
              }}
              className={`p-4 border-2 rounded-lg transition-all text-left ${
                tableDataSource === "manual"
                  ? "border-primary bg-primary/5 shadow-md"
                  : "border-border hover:border-primary/50 hover:bg-accent"
              }`}
            >
              <div className="text-2xl mb-2">✏️</div>
              <div className="font-medium text-sm">Manual</div>
              <div className="text-xs text-muted-foreground mt-1">
                Edite os dados diretamente
              </div>
            </button>

            <button
              type="button"
              onClick={() => {
                setTableDataSource("googleSheets");
                const currentDataSource = editedComponent.config.dataSource || {};
                updateConfig("dataSource", {
                  ...currentDataSource,
                  data: [],
                });
                updateConfig("googleSheets", {
                  enabled: true,
                  integrationId:
                    editedComponent.config.googleSheets?.integrationId || "",
                  range:
                    editedComponent.config.googleSheets?.range || "Sheet1",
                });
              }}
              className={`p-4 border-2 rounded-lg transition-all text-left ${
                tableDataSource === "googleSheets"
                  ? "border-green-500 bg-green-50 dark:bg-green-950 shadow-md"
                  : "border-border hover:border-green-500/50 hover:bg-accent"
              }`}
            >
              <div className="text-2xl mb-2">📊</div>
              <div className="font-medium text-sm">Google Sheets</div>
              <div className="text-xs text-muted-foreground mt-1">
                Sincronize com planilha
              </div>
            </button>

            <button
              type="button"
              onClick={() => {
                setTableDataSource("excel");
                updateConfig("googleSheets", {
                  ...editedComponent.config.googleSheets,
                  enabled: false,
                });
                updateConfig("restApi", {
                  ...editedComponent.config.restApi,
                  enabled: false,
                });
              }}
              className={`p-4 border-2 rounded-lg transition-all text-left ${
                tableDataSource === "excel"
                  ? "border-blue-500 bg-blue-50 dark:bg-blue-950 shadow-md"
                  : "border-border hover:border-blue-500/50 hover:bg-accent"
              }`}
            >
              <div className="text-2xl mb-2">📤</div>
              <div className="font-medium text-sm">Importar Excel</div>
              <div className="text-xs text-muted-foreground mt-1">
                Upload de arquivo .xlsx
              </div>
            </button>

            {isSuperAdmin && (
              <button
                type="button"
                onClick={() => {
                  setTableDataSource("restApi");
                  updateConfig("googleSheets", {
                    ...editedComponent.config.googleSheets,
                    enabled: false,
                  });
                  updateConfig("restApi", {
                    enabled: true,
                    integrationId:
                      editedComponent.config.restApi?.integrationId || "",
                    responsePath:
                      editedComponent.config.restApi?.responsePath || "",
                  });
                }}
                className={`p-4 border-2 rounded-lg transition-all text-left ${
                  tableDataSource === "restApi"
                    ? "border-purple-500 bg-purple-50 dark:bg-purple-950 shadow-md"
                    : "border-border hover:border-purple-500/50 hover:bg-accent"
                }`}
              >
                <div className="text-2xl mb-2">🔌</div>
                <div className="font-medium text-sm">API REST</div>
                <div className="text-xs text-muted-foreground mt-1">
                  Chamada a API externa
                </div>
              </button>
            )}
          </div>
        </div>

        {/* REST API Configuration */}
        {tableDataSource === "restApi" && (
          <div className="p-4 bg-gradient-to-br from-purple-50 to-violet-50 dark:from-purple-950 dark:to-violet-950 border-2 border-purple-500/30 rounded-lg space-y-3">
            <div>
              <h3 className="text-sm font-semibold flex items-center gap-2">
                🔌 API REST — selecione a integração
              </h3>
              <p className="text-xs text-muted-foreground mt-1">
                Escolha uma integração criada em Configurações → Integrações
              </p>
            </div>

            <div className="space-y-3 pt-1">
              {restApiIntegrations.length === 0 ? (
                <div className="p-3 bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 rounded text-xs text-amber-700 dark:text-amber-300 space-y-1">
                  <p className="font-semibold">
                    ⚠️ Nenhuma integração de API REST encontrada
                  </p>
                  <p>
                    Vá em{" "}
                    <strong>Configurações → Integrações</strong> e crie uma
                    integração do tipo API REST.
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  <Label htmlFor="restApiIntegration" className="text-xs">
                    Integração
                  </Label>
                  <Select
                    value={
                      editedComponent.config.restApi?.integrationId ||
                      "__none__"
                    }
                    onValueChange={(value) => {
                      if (value === "__none__") return;
                      updateConfig("restApi", {
                        ...editedComponent.config.restApi,
                        integrationId: value,
                      });
                    }}
                  >
                    <SelectTrigger id="restApiIntegration" className="text-xs">
                      <SelectValue placeholder="Selecione uma integração" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">
                        Selecione uma integração
                      </SelectItem>
                      {restApiIntegrations.map((i) => (
                        <SelectItem key={i.id} value={i.id}>
                          {i.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="space-y-1.5">
                <Label htmlFor="restApiPath" className="text-xs">
                  Caminho da resposta{" "}
                  <span className="text-muted-foreground font-normal">
                    (opcional)
                  </span>
                </Label>
                <Input
                  id="restApiPath"
                  type="text"
                  placeholder="Ex: data, data.items, results"
                  value={editedComponent.config.restApi?.responsePath || ""}
                  onChange={(e) =>
                    updateConfig("restApi", {
                      ...editedComponent.config.restApi,
                      responsePath: e.target.value,
                    })
                  }
                  className="text-xs font-mono"
                />
                <p className="text-xs text-muted-foreground">
                  Se a API retornar um objeto, indique o campo que contém o
                  array. Ex:{" "}
                  <code className="bg-muted px-1 rounded">data</code>
                </p>
              </div>

              <div className="flex items-start justify-between gap-3 pt-3 border-t border-purple-500/20">
                <div className="space-y-0.5">
                  <Label htmlFor="restApiPersist" className="text-xs font-medium">
                    Salvar dados no banco
                  </Label>
                  <p className="text-[11px] text-muted-foreground">
                    Busca a API uma vez e guarda no banco. Atualize manualmente
                    pelo botão "Sincronizar" na tabela.
                  </p>
                </div>
                <Switch
                  id="restApiPersist"
                  checked={editedComponent.config.restApi?.persistToDatabase ?? false}
                  onCheckedChange={(checked) =>
                    updateConfig("restApi", {
                      ...editedComponent.config.restApi,
                      persistToDatabase: checked,
                    })
                  }
                />
              </div>

              <div className="text-xs text-muted-foreground space-y-1 pt-2 border-t border-purple-500/20">
                <p>
                  {editedComponent.config.restApi?.persistToDatabase
                    ? "💾 Dados salvos no banco — buscados só na primeira vez ou ao sincronizar"
                    : "🔄 Dados buscados ao carregar a página"}
                </p>
                <p>
                  🔒 As colunas são criadas automaticamente com base na
                  resposta
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Google Sheets Configuration */}
        {tableDataSource === "googleSheets" && (
          <div className="p-4 bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-950 dark:to-emerald-950 border-2 border-green-500/30 rounded-lg space-y-3">
            <div>
              <h3 className="text-sm font-semibold flex items-center gap-2">
                📊 Google Sheets — selecione a integração
              </h3>
              <p className="text-xs text-muted-foreground mt-1">
                Escolha uma integração criada em Configurações → Integrações
              </p>
            </div>

            <div className="space-y-3 pt-1">
              {sheetsIntegrations.length === 0 ? (
                <div className="p-3 bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 rounded text-xs text-amber-700 dark:text-amber-300 space-y-1">
                  <p className="font-semibold">
                    ⚠️ Nenhuma integração de Google Sheets encontrada
                  </p>
                  <p>
                    Vá em{" "}
                    <strong>Configurações → Integrações</strong> e crie uma
                    integração do tipo Google Sheets.
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  <Label htmlFor="sheetsIntegration" className="text-xs">
                    Integração
                  </Label>
                  <Select
                    value={
                      editedComponent.config.googleSheets?.integrationId ||
                      "__none__"
                    }
                    onValueChange={(value) => {
                      if (value === "__none__") return;
                      updateConfig("googleSheets", {
                        ...editedComponent.config.googleSheets,
                        integrationId: value,
                      });
                      const integration = sheetsIntegrations.find(
                        (i) => i.id === value,
                      );
                      if (integration?.config?.spreadsheetId) {
                        handleFetchSheets(integration.config.spreadsheetId);
                      }
                    }}
                  >
                    <SelectTrigger id="sheetsIntegration" className="text-xs">
                      <SelectValue placeholder="Selecione uma integração" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">
                        Selecione uma integração
                      </SelectItem>
                      {sheetsIntegrations.map((i) => (
                        <SelectItem key={i.id} value={i.id}>
                          {i.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Sheet / range selection */}
              <div className="space-y-2">
                <Label htmlFor="tableSheetRange" className="text-xs">
                  Aba (Sheet)
                </Label>
                {loadingSheets ? (
                  <div className="flex items-center gap-2 p-2 bg-accent rounded">
                    <RefreshCw className="w-3 h-3 animate-spin" />
                    <span className="text-xs text-muted-foreground">
                      Buscando abas...
                    </span>
                  </div>
                ) : availableSheets.length > 0 ? (
                  <Select
                    value={
                      editedComponent.config.googleSheets?.range || "__none__"
                    }
                    onValueChange={(value) => {
                      if (value === "__none__") return;
                      updateConfig("googleSheets", {
                        ...editedComponent.config.googleSheets,
                        range: value,
                      });
                    }}
                  >
                    <SelectTrigger id="tableSheetRange" className="text-xs">
                      <SelectValue placeholder="Selecione uma aba" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">
                        Selecione uma aba
                      </SelectItem>
                      {availableSheets.map((sheetName) => (
                        <SelectItem key={sheetName} value={sheetName}>
                          {sheetName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    id="tableSheetRange"
                    type="text"
                    placeholder="Sheet1"
                    value={
                      editedComponent.config.googleSheets?.range || "Sheet1"
                    }
                    onChange={(e) => {
                      updateConfig("googleSheets", {
                        ...editedComponent.config.googleSheets,
                        range: e.target.value || "Sheet1",
                      });
                    }}
                    className="text-xs"
                  />
                )}
                {sheetFetchError && !loadingSheets && (
                  <p className="text-xs text-red-600 dark:text-red-400">
                    {sheetFetchError}
                  </p>
                )}
                {availableSheets.length > 0 && (
                  <p className="text-xs text-green-600 dark:text-green-400">
                    ✓ {availableSheets.length} abas disponíveis
                  </p>
                )}
              </div>

              <div className="text-xs text-muted-foreground space-y-1 pt-2 border-t border-green-500/20">
                <p>🔄 Dados sincronizados automaticamente com a planilha</p>
                <p>✅ Leitura e escrita gerenciadas pelo servidor</p>
              </div>
            </div>
          </div>
        )}

        {/* Upload Excel */}
        {tableDataSource === "excel" && (
          <div className="p-4 bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-950 dark:to-cyan-950 border-2 border-blue-500/30 rounded-lg space-y-3">
            <div>
              <h3 className="text-sm font-semibold flex items-center gap-2">
                <Upload className="w-4 h-4 text-primary" />
                Importar do Excel
              </h3>
              <p className="text-xs text-muted-foreground mt-1">
                Envie um arquivo .xlsx ou .xls para criar colunas e preencher
                dados automaticamente
              </p>
            </div>

            <div className="space-y-2">
              <Label className="text-xs font-medium">Modo de Importação:</Label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    value="replace"
                    checked={uploadMode === "replace"}
                    onChange={(e) =>
                      setUploadMode(e.target.value as "replace" | "add")
                    }
                    className="w-4 h-4"
                  />
                  <span className="text-sm">
                    Substituir dados
                    <span className="block text-xs text-muted-foreground">
                      Remove dados antigos
                    </span>
                  </span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    value="add"
                    checked={uploadMode === "add"}
                    onChange={(e) =>
                      setUploadMode(e.target.value as "replace" | "add")
                    }
                    className="w-4 h-4"
                  />
                  <span className="text-sm">
                    Adicionar dados
                    <span className="block text-xs text-muted-foreground">
                      Mantém dados existentes
                    </span>
                  </span>
                </label>
              </div>
            </div>

            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={handleExcelUpload}
              className="block w-full text-sm text-muted-foreground file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-600 file:text-white hover:file:bg-blue-700 file:cursor-pointer"
            />
          </div>
        )}

        {tableDataSource === "manual" && (
          <div className="p-4 bg-accent/50 border border-primary/20 rounded-md">
            <p className="text-sm">
              <strong>💡 Dica:</strong> Os dados da tabela podem ser editados
              clicando no botão "Editar Dados" que aparece no próprio
              componente.
            </p>
          </div>
        )}

        {tableDataSource === "googleSheets" && (
          <div className="p-4 bg-amber-50 dark:bg-amber-950 border border-amber-500/30 rounded-md">
            <p className="text-sm">
              <strong>⚠️ Atenção:</strong> As colunas serão criadas
              automaticamente com base nos cabeçalhos da planilha. A edição
              manual de colunas está desabilitada para fontes do Google Sheets.
            </p>
          </div>
        )}

        {tableDataSource === "restApi" && (
          <div className="p-4 bg-amber-50 dark:bg-amber-950 border border-amber-500/30 rounded-md">
            <p className="text-sm">
              <strong>⚠️ Atenção:</strong> As colunas serão criadas
              automaticamente com base na primeira resposta da API. A edição
              manual de colunas está desabilitada para fontes de API REST.
            </p>
          </div>
        )}
      </TabsContent>

      <TabsContent value="colunas" className="space-y-4">
        {isGoogleSheetsActive ? (
          <div className="p-8 text-center">
            <div className="text-4xl mb-4">🔒</div>
            <h3 className="font-medium text-lg mb-2">Colunas Automáticas</h3>
            <p className="text-sm text-muted-foreground max-w-md mx-auto">
              As colunas são gerenciadas automaticamente pelo Google Sheets. Os
              cabeçalhos da planilha definem as colunas da tabela.
            </p>
            <p className="text-xs text-muted-foreground mt-4">
              Para editar as colunas, altere os cabeçalhos diretamente na
              planilha do Google Sheets.
            </p>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-medium">Configuração de Colunas</h3>
                <p className="text-xs text-muted-foreground">
                  Defina os campos e tipos de dados da tabela
                </p>
              </div>
              <Button
                type="button"
                size="sm"
                onClick={() => {
                  const hasId = columns.some((c: any) => c.type === 'id');
                  const newColumns = hasId
                    ? [...columns, { field: '', label: '', visible: true }]
                    : [
                        { field: 'id', label: 'ID', type: 'id', visible: false },
                        ...columns,
                        { field: '', label: '', visible: true },
                      ];
                  updateConfig('columns', newColumns);
                }}
              >
                <Plus className="w-3 h-3 mr-1" />
                Adicionar Coluna
              </Button>
            </div>

            <div className="space-y-3 max-h-[450px] overflow-y-auto pr-2">
              {columns.map((col: any, index: number) => (
                <div
                  key={index}
                  className={`border rounded-lg ${col.type === "lookup" ? "bg-muted/30" : "bg-card"}`}
                >
                  {col.type === 'id' ? (
                    /* Coluna ID — obrigatória e somente leitura */
                    <div className="flex items-center gap-2 p-2 bg-muted/40">
                      <Lock className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                      <span className="text-sm font-medium text-muted-foreground flex-1">
                        ID
                      </span>
                      <span className="text-xs text-muted-foreground px-2 py-1 bg-muted rounded border">
                        Obrigatório
                      </span>
                    </div>
                  ) : col.type !== 'lookup' ? (
                    <div className="flex items-center gap-2 p-2">
                      <Input
                        type="text"
                        placeholder="Nome da coluna"
                        value={col.label || ''}
                        onChange={(e) => {
                          const newColumns = [...columns];
                          newColumns[index] = {
                            ...newColumns[index],
                            label: e.target.value,
                            field: generateField(e.target.value, index),
                          };
                          updateConfig('columns', newColumns);
                        }}
                        className="h-8 text-sm flex-1 min-w-[120px]"
                      />
                      <Select
                        value={col.type || 'text'}
                        onValueChange={(value) => {
                          const newColumns = [...columns];
                          newColumns[index] = {
                            ...newColumns[index],
                            type: value,
                          };
                          if (value !== 'lookup') {
                            delete newColumns[index].lookupTable;
                          }
                          updateConfig('columns', newColumns);
                        }}
                      >
                        <SelectTrigger className="h-8 text-sm w-[150px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="text">Texto</SelectItem>
                          <SelectItem value="number">Número</SelectItem>
                          <SelectItem value="date">Data</SelectItem>
                          <SelectItem value="badge">Badge</SelectItem>
                          <SelectItem value="lookup">🔗 Lookup</SelectItem>
                          <SelectItem value="tableLink">📊 Link Planilha</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          const newColumns = columns.filter(
                            (_: any, i: number) => i !== index,
                          );
                          updateConfig('columns', newColumns);
                        }}
                        className="h-8 w-8 text-destructive hover:bg-destructive/10 flex-shrink-0"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ) : (
                    /* Coluna Lookup */
                    <>
                      <div className="flex items-center gap-2 p-3 bg-muted/50 border-b">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">
                            🔗 {col.label || "Lookup"}
                            <span className="text-xs text-muted-foreground ml-2">
                              ({col.field || "sem-campo"})
                            </span>
                          </p>
                          <p className="text-xs text-muted-foreground">
                            Relacionamento com outra tabela
                          </p>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            const newColumns = columns.filter(
                              (_: any, i: number) => i !== index,
                            );
                            updateConfig("columns", newColumns);
                          }}
                          className="text-destructive hover:bg-destructive/10 flex-shrink-0"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>

                      <div className="p-3 space-y-3">
                        <p className="text-xs font-semibold text-primary mb-2">
                          ⚙️ Configuração de Relacionamento
                        </p>

                        <div className="space-y-1">
                          <Label className="text-xs">Tabela Referenciada</Label>
                          <Select
                            value={col.lookupTable?.tableId || ""}
                            onValueChange={(value) => {
                              const selectedTable = availableComponents.find(
                                (c) => c.id === value,
                              );
                              const newColumns = [...columns];
                              const tableName =
                                selectedTable?.config?.title || "table";
                              const fieldName =
                                tableName
                                  .toLowerCase()
                                  .replace(/\s+/g, "") + "Id";
                              newColumns[index] = {
                                ...newColumns[index],
                                field: fieldName,
                                label: tableName,
                                lookupTable: {
                                  ...(newColumns[index].lookupTable || {}),
                                  tableId: value,
                                },
                              };
                              updateConfig("columns", newColumns);
                            }}
                          >
                            <SelectTrigger className="h-8 text-sm">
                              <SelectValue placeholder="Selecione a tabela" />
                            </SelectTrigger>
                            <SelectContent>
                              {availableComponents
                                .filter(
                                  (c) =>
                                    c.type === "table" &&
                                    c.id !== editedComponent.id,
                                )
                                .map((table) => {
                                  const pageName = (table as any)._pageName;
                                  const displayName = pageName
                                    ? `${table.config.title} (${pageName})`
                                    : table.config.title;
                                  return (
                                    <SelectItem
                                      key={table.id}
                                      value={table.id}
                                    >
                                      {displayName}
                                    </SelectItem>
                                  );
                                })}
                            </SelectContent>
                          </Select>
                        </div>

                        {col.lookupTable?.tableId &&
                          (() => {
                            const lookupTable = availableComponents.find(
                              (c) => c.id === col.lookupTable.tableId,
                            );
                            const lookupColumns =
                              lookupTable?.config?.columns || [];

                            return (
                              <>
                                <div className="space-y-1">
                                  <Label className="text-xs">
                                    Campo Chave (ID)
                                  </Label>
                                  <Select
                                    value={col.lookupTable?.keyField || ""}
                                    onValueChange={(value) => {
                                      const newColumns = [...columns];
                                      newColumns[index] = {
                                        ...newColumns[index],
                                        lookupTable: {
                                          ...newColumns[index].lookupTable,
                                          keyField: value,
                                        },
                                      };
                                      updateConfig("columns", newColumns);
                                    }}
                                  >
                                    <SelectTrigger className="h-8 text-sm">
                                      <SelectValue placeholder="Campo ID" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {lookupColumns.map((lcol: any) => (
                                        <SelectItem
                                          key={lcol.field}
                                          value={lcol.field}
                                        >
                                          {lcol.label}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>

                                <div className="space-y-1">
                                  <Label className="text-xs">
                                    Campo a Exibir
                                  </Label>
                                  <Select
                                    value={
                                      col.lookupTable?.displayField || ""
                                    }
                                    onValueChange={(value) => {
                                      const newColumns = [...columns];
                                      newColumns[index] = {
                                        ...newColumns[index],
                                        lookupTable: {
                                          ...newColumns[index].lookupTable,
                                          displayField: value,
                                        },
                                      };
                                      updateConfig("columns", newColumns);
                                    }}
                                  >
                                    <SelectTrigger className="h-8 text-sm">
                                      <SelectValue placeholder="Campo a mostrar" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {lookupColumns.map((lcol: any) => (
                                        <SelectItem
                                          key={lcol.field}
                                          value={lcol.field}
                                        >
                                          {lcol.label}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>

                                <div className="space-y-1">
                                  <Label className="text-xs">
                                    Valor Padrão (Opcional)
                                  </Label>
                                  <Select
                                    value={
                                      col.defaultValue?.toString() || "none"
                                    }
                                    onValueChange={(value) => {
                                      const newColumns = [...columns];
                                      newColumns[index] = {
                                        ...newColumns[index],
                                        defaultValue:
                                          value === "none"
                                            ? undefined
                                            : value,
                                      };
                                      updateConfig("columns", newColumns);

                                      if (value !== "none") {
                                        const currentData =
                                          editedComponent.config.dataSource
                                            ?.data || [];
                                        const fieldName = col.field;
                                        const updatedData = currentData.map(
                                          (row: any) => {
                                            if (
                                              !row[fieldName] ||
                                              row[fieldName] === "" ||
                                              row[fieldName] === null
                                            ) {
                                              return {
                                                ...row,
                                                [fieldName]: value,
                                              };
                                            }
                                            return row;
                                          },
                                        );

                                        if (
                                          JSON.stringify(currentData) !==
                                          JSON.stringify(updatedData)
                                        ) {
                                          setEditedComponent({
                                            ...editedComponent,
                                            config: {
                                              ...editedComponent.config,
                                              dataSource: {
                                                type: "static",
                                                ...editedComponent.config
                                                  .dataSource,
                                                data: updatedData,
                                              },
                                            },
                                          });
                                        }
                                      }
                                    }}
                                  >
                                    <SelectTrigger className="h-8 text-sm">
                                      <SelectValue placeholder="Nenhum" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="none">
                                        Nenhum
                                      </SelectItem>
                                      {(
                                        lookupTable?.config?.dataSource
                                          ?.data || []
                                      ).map((item: any) => {
                                        const keyValue =
                                          item[col.lookupTable?.keyField];
                                        const displayValue =
                                          item[
                                            col.lookupTable?.displayField
                                          ];
                                        return (
                                          <SelectItem
                                            key={keyValue}
                                            value={keyValue?.toString()}
                                          >
                                            {displayValue}
                                          </SelectItem>
                                        );
                                      })}
                                    </SelectContent>
                                  </Select>
                                  <p className="text-xs text-muted-foreground">
                                    Preenche automaticamente registros vazios e
                                    novos
                                  </p>
                                </div>
                              </>
                            );
                          })()}
                      </div>
                    </>
                  )}
                </div>
              ))}
              {columns.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-8">
                  Nenhuma coluna adicionada. Clique em "Adicionar Coluna" para
                  começar.
                </p>
              )}
            </div>
          </>
        )}
      </TabsContent>

      <TabsContent value="exibicao" className="space-y-4">
        <div>
          <h3 className="font-medium mb-1">Configurações de Exibição</h3>
          <p className="text-xs text-muted-foreground">
            Personalize a aparência e comportamento da tabela
          </p>
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between p-3 border rounded-lg">
            <div>
              <Label htmlFor="pagination" className="text-sm font-medium">
                Paginação
              </Label>
              <p className="text-xs text-muted-foreground">
                Divide a tabela em páginas
              </p>
            </div>
            <input
              id="pagination"
              type="checkbox"
              checked={editedComponent.config.pagination ?? false}
              onChange={(e) => updateConfig("pagination", e.target.checked)}
              className="w-4 h-4 rounded"
            />
          </div>

          {editedComponent.config.pagination && (
            <div className="space-y-2 pl-4 border-l-2 border-primary/30">
              <Label htmlFor="pageSize" className="text-sm">
                Registros por página
              </Label>
              <Input
                id="pageSize"
                type="number"
                value={editedComponent.config.pageSize || 10}
                onChange={(e) =>
                  updateConfig("pageSize", Number(e.target.value))
                }
                min="1"
                max="100"
                className="max-w-[120px]"
              />
            </div>
          )}

          <div className="flex items-center justify-between p-3 border rounded-lg">
            <div>
              <Label htmlFor="striped" className="text-sm font-medium">
                Linhas zebradas
              </Label>
              <p className="text-xs text-muted-foreground">
                Alterna cor de fundo nas linhas
              </p>
            </div>
            <input
              id="striped"
              type="checkbox"
              checked={editedComponent.config.striped ?? true}
              onChange={(e) => updateConfig("striped", e.target.checked)}
              className="w-4 h-4 rounded"
            />
          </div>

          <div className="flex items-center justify-between p-3 border rounded-lg">
            <div>
              <Label htmlFor="hoverable" className="text-sm font-medium">
                Hover nas linhas
              </Label>
              <p className="text-xs text-muted-foreground">
                Destaca linha ao passar o mouse
              </p>
            </div>
            <input
              id="hoverable"
              type="checkbox"
              checked={editedComponent.config.hoverable ?? true}
              onChange={(e) => updateConfig("hoverable", e.target.checked)}
              className="w-4 h-4 rounded"
            />
          </div>

          <div className="flex items-center justify-between p-3 border rounded-lg">
            <div>
              <Label htmlFor="compact" className="text-sm font-medium">
                Modo compacto
              </Label>
              <p className="text-xs text-muted-foreground">
                Reduz o espaçamento interno
              </p>
            </div>
            <input
              id="compact"
              type="checkbox"
              checked={editedComponent.config.compact ?? false}
              onChange={(e) => updateConfig("compact", e.target.checked)}
              className="w-4 h-4 rounded"
            />
          </div>
        </div>
      </TabsContent>
    </Tabs>
  );
}
